/**
 * Shared color name to hex mapping.
 * Used by both the color swatch UI and the 3D model viewer.
 *
 * Resolution handles multi-word names like "Indigo Purple" by
 * matching individual words against known color keys.
 */

const COLOR_MAP: Readonly<Record<string, string>> = {
  white: '#f0f0f0',
  black: '#1a1a1a',
  red: '#dc2626',
  blue: '#2563eb',
  green: '#16a34a',
  yellow: '#eab308',
  orange: '#ea580c',
  purple: '#9333ea',
  pink: '#ec4899',
  gray: '#6b7280',
  grey: '#6b7280',
  silver: '#94a3b8',
  gold: '#d97706',
  natural: '#e8dcc8',
  clear: '#e0f2fe',
  navy: '#1e3a5f',
  brown: '#78350f',
  beige: '#d4c5a9',
  teal: '#0d9488',
  olive: '#65a30d',
  maroon: '#7f1d1d',
  cyan: '#06b6d4',
  indigo: '#4f46e5',
};

const FALLBACK = '#6b7280';

/**
 * Resolve a color name (possibly multi-word) to a CSS hex string.
 *
 * 1. Try exact match ("red", "indigo")
 * 2. Try each word in order ("Indigo Purple" -> tries "indigo" first)
 * 3. Fall back to gray
 */
export const resolveColorHex = (colorName: string): string => {
  const lower = colorName.toLowerCase();
  if (COLOR_MAP[lower]) return COLOR_MAP[lower];

  const words = lower.split(/[\s-]+/);
  for (const word of words) {
    if (COLOR_MAP[word]) return COLOR_MAP[word];
  }

  return FALLBACK;
};

export const isLightColor = (hex: string): boolean => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
};
